/**
 * Comm-surface icons (screen-local, v3 "AFTER-DARK ARCADE").
 *
 * Crisp inline SVG for the communication screens only (chat / conversations /
 * call / roulette import from here — same cross-feature convention as
 * `Avatar` in ../conversations/matches.js). Shared brand marks stay in
 * design/icons.tsx (FOUNDATION); these are the utilitarian comm glyphs the
 * foundation set doesn't carry. No icon fonts, no CDNs (CSP 'self').
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps) {
  return {
    viewBox: "0 0 24 24",
    width: 20,
    height: 20,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    ...props,
  };
}

/** Back chevron. */
export function IconChevronLeft(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </svg>
  );
}

/** Paper-plane send. */
export function IconSend(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M4 11.5 20 4l-4.5 16-4-6.5L4 11.5z" />
      <path d="M11.5 13.5 20 4" strokeWidth="1.4" opacity="0.7" />
    </svg>
  );
}

/** GIF tile. */
export function IconGif(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M9.4 10.2a1.9 1.9 0 0 0-1.7-.9c-1.2 0-2 .95-2 2.7s.8 2.7 2 2.7c1.1 0 1.8-.7 1.8-1.8h-1.6" strokeWidth="1.5" />
      <path d="M12.4 9.4v5.2" strokeWidth="1.5" />
      <path d="M15.2 14.6V9.4h3.2M15.2 12h2.5" strokeWidth="1.5" />
    </svg>
  );
}

/** Photo / image frame. */
export function IconImage(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <circle cx="9" cy="10" r="1.7" />
      <path d="M4.5 17.5 10 12.5l3 2.8 3.4-3.3 3.1 3" />
    </svg>
  );
}

/** Microphone. */
export function IconMic(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="9.2" y="3.5" width="5.6" height="10" rx="2.8" />
      <path d="M6 11.5a6 6 0 0 0 12 0" />
      <path d="M12 17.5v3" />
    </svg>
  );
}

/** Play triangle (voice notes). */
export function IconPlay(props: IconProps) {
  return (
    <svg {...base(props)} fill="currentColor" stroke="none">
      <path d="M8.2 5.8c0-.86.94-1.39 1.68-.95l9.1 5.7a1.12 1.12 0 0 1 0 1.9l-9.1 5.7a1.12 1.12 0 0 1-1.68-.95V5.8z" />
    </svg>
  );
}

/** Pause bars (voice notes). */
export function IconPause(props: IconProps) {
  return (
    <svg {...base(props)} fill="currentColor" stroke="none">
      <rect x="7" y="5" width="3.6" height="14" rx="1.3" />
      <rect x="13.4" y="5" width="3.6" height="14" rx="1.3" />
    </svg>
  );
}

/** Handset (voice call). */
export function IconPhone(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M6.8 4.2c.6-.6 1.6-.5 2.1.2l1.6 2.3c.4.6.4 1.4-.1 1.9l-1 1.1a12.6 12.6 0 0 0 4.9 4.9l1.1-1c.5-.5 1.3-.6 1.9-.1l2.3 1.6c.7.5.8 1.5.2 2.1l-1.2 1.2c-.7.7-1.7 1-2.6.7-2.7-.8-5.3-2.4-7.4-4.5S5.3 9.9 4.5 7.2c-.3-.9 0-1.9.7-2.6l1.6-.4z" />
    </svg>
  );
}

/** Handset, hung up (decline / hang up). */
export function IconPhoneDown(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M3.2 13.9c-.6-.6-.6-1.6 0-2.2 2.3-2.2 5.4-3.5 8.8-3.5s6.5 1.3 8.8 3.5c.6.6.6 1.6 0 2.2l-1.3 1.3c-.5.5-1.4.6-2 .2l-2.2-1.5c-.5-.4-.8-1-.7-1.6l.1-1.1a9.9 9.9 0 0 0-5.4 0l.1 1.1c.1.6-.2 1.2-.7 1.6l-2.2 1.5c-.6.4-1.5.3-2-.2l-1.3-1.3z" />
    </svg>
  );
}

/** Camera (video call). */
export function IconVideo(props: IconProps) {
  return (
    <svg {...base(props)}>
      <rect x="3" y="7" width="12" height="10" rx="2.5" />
      <path d="m15 10.5 4.6-2.6a.8.8 0 0 1 1.2.7v6.8a.8.8 0 0 1-1.2.7L15 13.5" />
    </svg>
  );
}

/** Camera off. */
export function IconVideoOff(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9.5 7H13a2.5 2.5 0 0 1 2.5 2.5v.5l4.1-2.3a.8.8 0 0 1 1.2.7v6.8a.8.8 0 0 1-1.2.7l-4.1-2.4v.6" />
      <path d="M3 8.8v5.7A2.5 2.5 0 0 0 5.5 17h7" />
      <path d="M4 4l16 16" />
    </svg>
  );
}

/** Mic muted. */
export function IconMicOff(props: IconProps) {
  return (
    <svg {...base(props)}>
      <path d="M9.2 6.3a2.8 2.8 0 0 1 5.6 0v4.9M9.2 10v1.2a2.8 2.8 0 0 0 4.5 2.2" />
      <path d="M6 11.5a6 6 0 0 0 9.6 4.8M17.6 13.6c.26-.65.4-1.36.4-2.1" />
      <path d="M12 17.5v3" />
      <path d="M4.5 4.5l15 15" />
    </svg>
  );
}

/** Three-dots overflow. */
export function IconDots(props: IconProps) {
  return (
    <svg {...base(props)} fill="currentColor" stroke="none">
      <circle cx="5.5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18.5" cy="12" r="1.6" />
    </svg>
  );
}

/** Small lightning bolt (opening move). */
export function IconBolt(props: IconProps) {
  return (
    <svg {...base(props)} fill="currentColor" stroke="none">
      <path d="M13.2 3.2 5.8 13h4.4l-1.4 7.8L16.6 11h-4.6l1.2-7.8z" />
    </svg>
  );
}
